import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

// Libraries
import PerfectScrollbar from 'react-perfect-scrollbar';

// Components
import DiscardPortfolioModal from '@/views/components/modals/DiscardPortfolioModal';
import { withErrorBoundary } from '@/views/components/errors';
import SaveUnallocatedPortfolioModal from '@/views/components/modals/SaveUnallocatedPortfolioModal';
import ManualProxyReplacementWarningModal from '@/views/components/modals/ManualProxyReplacementWarningModal';
import ClearHoldingsModal from '@/views/components/modals/portfolio-modals/ClearPortfolioHoldingsModal';
import FullPageModal from '../FullPageModal';
import {
  PortfolioEditorFooter,
  PortfolioEditorHeader,
  PortfolioEditorContent,
  PortfolioEditorOverview,
  PortfolioEditorPlanAccountModal,
  InformationReplacedModal,
} from './components';
import Modal from '@/views/components/modals/AccessibleModal';

// Hooks
import { usePortfolioEditorPlanAccount } from './hooks/usePortfolioEditorPlanAccount';
import { useAssetTypeCache } from './hooks/useAssetTypeCache';

// Redux
import { attachProfileToPortfolio, setPortfolioPlanAccount, setPortfolioSize, resetPortfolio } from '@/redux/modules/edit-portfolio';
import { getFundData } from '@/redux/modules/funds/fund-store';
import { getStockData } from '@/redux/modules/stocks/stock-store';
import { getAltData } from '@/redux/modules/alts/alts-store';
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';
import {
  processNextPortfolio,
  resetMultiPortfolio,
  hasNextPortfolio,
  isInMultiPortfolioMode,
  addSavedPortfolioId,
} from '@/redux/modules/ai-statement-reader-multi-portfolio';

// Types
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import Alt from '@/types/models/alt';
import Holding from '@/types/holding';
import PortfolioHolding from '@/types/portfolio-holding';
import Portfolio from '@/types/portfolio';
import { ReduxState } from '@/redux/modules/types';
import GenericContact from '@/types/generic-contact';
import { Account } from '@/types/planning';
import PortfolioRiskProfile from '@/types/portfolio-risk-profile';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { GenericHouseHold } from '@/types/households';
import ProxyLogEntry from '@/types/portfolio-log-entry';

// Utils
import notification from '@/utils/notification';
import Shelf from '@/types/shelf';
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { canAccessMultiPortfoliosStatementReader } from '@/utils/feature-access';
import { formatMessage } from '@/utils/i18n';
import { getTotalPortfolioAllocation, PortfolioEditorContext, portfolioHasMissingCostBasis, HOLDING_TYPES } from '@/utils/portfolios';
import { getPortfolioMissingOrInvalidHoldings } from '@/utils/portfolio/utils';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { showError } from '@/utils/errors';
import { redirectToPortfolios } from './utils/redirect';
import { getCachedCustomYieldFund } from '@/utils/portfolio/customYieldCache';

type StateProps = {
  isDirty: boolean;
  isSaving: boolean;
  portfolio: Portfolio;
  portfolioHasMissingHoldings: boolean;
  contact: GenericContact;
  planId?: string;
  planAccountId?: string;
  user: Nullable<User>;
  editorContext?: PortfolioEditorContext;
  currentHousehold?: GenericHouseHold;
  showTaxDetails?: boolean;
  hasMorePortfolios: boolean;
  isInMultiPortfolioMode: boolean;
  multiPortfolioCount: number;
  currentPortfolioIndex?: number;
};

type DispatchProps = {
  attachProfileToPortfolio: (profile: Partial<PortfolioRiskProfile>) => void;
  setPortfolioPlanAccount: (planId: string, planAccountId: string, planAccount: Account) => void;
  setPortfolioSize: (size: Nullable<number>) => void;
  getFundData: (id: string) => Promise<Fund>;
  getStockData: (id: string) => Promise<Stock>;
  getAltData: (id: string) => Promise<Alt>;
  processNextPortfolio: () => void;
  resetMultiPortfolio: () => void;
  resetPortfolio: (keepModalOpen?: boolean) => void;
  addSavedPortfolioId: (portfolioId: string) => void; // Add this type
};

type Props = {
  isComparisonReran: boolean;
  show: boolean;
  showActionOnSave?: boolean;
  onCancel: () => void;
  onExited?: () => void;
  onSave: (portfolio: Portfolio) => Promise<void>;
} & StateProps &
  DispatchProps;

const PortfolioEditorBuilderModal = ({
  isComparisonReran,
  isDirty,
  isSaving,
  portfolio,
  hasMorePortfolios,
  isInMultiPortfolioMode,
  processNextPortfolio,
  resetMultiPortfolio,
  resetPortfolio,
  multiPortfolioCount,
  currentPortfolioIndex,
  portfolioHasMissingHoldings,
  show,
  showActionOnSave,
  contact,
  planId,
  planAccountId,
  onCancel,
  onExited,
  onSave,
  setPortfolioPlanAccount,
  setPortfolioSize,
  attachProfileToPortfolio,
  user,
  editorContext,
  currentHousehold,
  showTaxDetails,
  getFundData,
  getStockData,
  getAltData,
  addSavedPortfolioId,
}: Props) => {
  const totalAllocated = getTotalPortfolioAllocation(portfolio);
  const [showDiscardPortfolioModal, setShowDiscardPortfolioModal] = useState<boolean>(false);
  const [showSaveUnallocatedPortfolioModal, setShowSaveUnallocatedPortfolioModal] = useState<boolean>(false);
  const [showManualProxyReplacementWarningModal, setShowManualProxyReplacementWarningModal] = useState<boolean>(false);
  const [showClearHoldingsModal, setShowClearHoldingsModal] = useState(false);
  const [proxyLog, setProxyLog] = useState<ProxyLogEntry[]>([]);
  const history = useHistory();
  const [showMissingCostBasisBanner, setShowMissingCostBasisBanner] = useState(false);
  const isMultiPortfoliosEnabled = canAccessMultiPortfoliosStatementReader(user) && isInMultiPortfolioMode;

  const getHoldingData = useCallback(
    async (holding: PortfolioHolding): Promise<Holding> => {
      const holdingId = holding.original.id;
      if (!holdingId) {
        return Promise.reject(new Error('Holding ID is missing'));
      }

      switch (holding.original.type) {
        case HOLDING_TYPES.FUND: {
          return getFundData(holdingId);
        }
        case HOLDING_TYPES.STOCK: {
          return getStockData(holdingId);
        }
        case HOLDING_TYPES.ALT: {
          return getAltData(holdingId);
        }
        case HOLDING_TYPES.CUSTOM_YIELD: {
          if (!holding.original.yieldParams) {
            return Promise.reject(new Error('Custom yield params missing'));
          }

          return getCachedCustomYieldFund(
            holding.original.yieldParams,
            { filteredShelves: holding.filteredShelves as Shelf[], complianceComparisons: holding.complianceComparisons },
            holding.currency,
            holdingId,
          );
        }
        default: {
          return Promise.reject(new Error('Unknown holding type'));
        }
      }
    },
    [getFundData, getStockData, getAltData],
  );

  const assetTypeCache = useAssetTypeCache({ portfolioHoldings: portfolio.holdings, getHoldingData });
  const isMissingCostBasis = showTaxDetails
    ? portfolioHasMissingCostBasis(portfolio.holdings, holding => {
        if ('original' in holding && holding.original.id) {
          return assetTypeCache.get(holding.original.id);
        }
        return undefined;
      })
    : false;

  const {
    showInformationReplacedModal,
    showPlanAccountModal,
    onAddPlanAccount,
    onConfirmPlanAccount,
    onCancelPlanAccount,
    onChangePlanAccount,
    onClosePlanAccountModal,
    selectedPlanAccount,
    importedAccounts,
  } = usePortfolioEditorPlanAccount({
    attachProfileToPortfolio,
    contact,
    setPortfolioPlanAccount,
    setPortfolioSize,
    user,
  });

  // The widget would overlap on the save button on smaller resolutions.
  // It was decided at that point to hide the widget on that screen since
  // design was not able to decide how to handle those smaller screens by
  // keeping the UI look consistent.
  useEffect(() => {
    if (show) {
      ZendeskUtils.hideWidget();
    }
    return () => {
      if (userHasPermission(user, PERMISSIONS.CHAT_SUPPORT)) {
        ZendeskUtils.showWidget();
      }
    };
  }, [show, user]);

  const handleOnCancel = () => {
    if (isDirty) {
      setShowDiscardPortfolioModal(true);
    } else {
      onCancel();
    }
  };

  const onConfirmDiscardPortfolio = () => {
    setShowDiscardPortfolioModal(false);

    // Clean up multi-portfolio state when discarding
    if (isInMultiPortfolioMode) {
      resetMultiPortfolio();
    }

    onCancel();
  };

  const handleFooterSave = async () => {
    if (userHasPermission(user, PERMISSIONS.PORTFOLIOS_ALLOW_UNALLOCATED) && totalAllocated < 100) {
      setShowSaveUnallocatedPortfolioModal(true);
    } else {
      await handleSaveAndClose();
    }
  };

  const handleSaveAndClose = async () => {
    const hasProxyUsedWithoutKey = proxyLog.length > 0 && proxyLog.some(entry => entry.proxyUsedWithoutKey);
    const shouldShowManualProxyReplacementWarningModal = userHasPermission(user, PERMISSIONS.MANUAL_PROXY_MODAL) && hasProxyUsedWithoutKey;

    if (shouldShowManualProxyReplacementWarningModal) {
      setShowManualProxyReplacementWarningModal(true);
      return;
    }

    if (isMissingCostBasis) {
      setShowMissingCostBasisBanner(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    // Strip lots from holdings if tax details are not enabled
    const portfolioToSave = showTaxDetails
      ? portfolio
      : {
          ...portfolio,
          holdings: portfolio.holdings.map(holding => {
            const { lots, ...holdingWithoutLots } = holding;
            return holdingWithoutLots;
          }),
        };

    if (proxyLog.length > 0) {
      portfolioToSave.metadata = {
        ...(portfolioToSave.metadata || {}),
        missingHoldingsProxyLog: proxyLog,
      };
    }

    const redirectPath = redirectToPortfolios(portfolio, currentHousehold, history.location.pathname, showActionOnSave);

    return onSave(portfolioToSave)
      .then(() => {
        // Track saved portfolio ID
        if (isInMultiPortfolioMode && portfolioToSave._id) {
          addSavedPortfolioId(portfolioToSave._id);
        }

        // Only show notification when not in multi-portfolio mode, or when saving the last portfolio
        if (!isInMultiPortfolioMode || !hasMorePortfolios) {
          notification.success(
            isInMultiPortfolioMode ? formatMessage('HoldingsEditor.Saved.MULTI_PORTFOLIOS') : formatMessage('HoldingsEditor.Saved.PORTFOLIO'),
            undefined,
            redirectPath && !hasMorePortfolios
              ? [
                  {
                    label: formatMessage('HoldingsEditor.Saved.PORTFOLIO_ACTION'),
                    onClick: () => history.push(redirectPath),
                  },
                ]
              : undefined,
          );
        }

        // Handle multi-portfolio carousel
        if (hasMorePortfolios) {
          // Load next portfolio instead of closing
          processNextPortfolio();
        } else if (isInMultiPortfolioMode) {
          // Clean up multi-portfolio state when done with carousel
          resetMultiPortfolio();
        }
      })
      .finally(() => {
        setShowSaveUnallocatedPortfolioModal(false);
        setShowManualProxyReplacementWarningModal(false);
      })
      .catch(showError);
  };

  const onConfirmManualProxyReplacementWarning = () => {
    setShowManualProxyReplacementWarningModal(false);
    performSave();
  };

  const displayClearHoldingsModal = () => {
    setShowClearHoldingsModal(true);
  };

  const hideClearHoldingsModal = () => {
    setShowClearHoldingsModal(false);
  };

  const allowUnallocatedPortfolio = userHasPermission(user, PERMISSIONS.PORTFOLIOS_ALLOW_UNALLOCATED) && editorContext === PortfolioEditorContext.PORTFOLIO;

  return (
    <>
      <FullPageModal show={show} className="portfolio-editor-builder-modal" onExited={onExited}>
        <PortfolioEditorHeader onCancel={handleOnCancel} />
        <PerfectScrollbar className="portfolio-editor-body-scrollbar-container">
          <div className="portfolio-editor-body-container">
            <Modal.Body className="portfolio-editor-body">
              <PortfolioEditorOverview isSaving={isSaving} onChangePlanAccount={onChangePlanAccount} />
              <PortfolioEditorContent
                showMissingCostBasisBanner={showMissingCostBasisBanner}
                totalAllocated={totalAllocated}
                onChangePlanAccount={onChangePlanAccount}
                onProxyLogChange={setProxyLog}
                isMissingCostBasis={isMissingCostBasis}
                assetTypeCache={assetTypeCache}
                getHoldingData={getHoldingData}
                displayClearHoldingsModal={displayClearHoldingsModal}
              />
            </Modal.Body>
          </div>
        </PerfectScrollbar>
        <PortfolioEditorFooter
          allowUnallocated={allowUnallocatedPortfolio}
          isComparisonReran={isComparisonReran}
          onSave={handleFooterSave}
          onCancel={onCancel}
          setShowMissingCostBasisBanner={setShowMissingCostBasisBanner}
          showMissingCostBasisBanner={showMissingCostBasisBanner}
          totalAllocated={totalAllocated}
          isMissingCostBasis={isMissingCostBasis}
        />
      </FullPageModal>

      {isMultiPortfoliosEnabled ? (
        <DiscardPortfolioModal
          backdropClassName="modal-v2-backdrop"
          body="PortfolioEditorBuilder.Footer.MultiPortfolios.DiscardMultiPortfoliosModal.Body"
          cancelLabel="PortfolioEditorBuilder.Footer.MultiPortfolios.DiscardMultiPortfoliosModal.CancelLabel"
          confirmLabel="PortfolioEditorBuilder.Footer.MultiPortfolios.DiscardMultiPortfoliosModal.ConfirmLabel"
          onCancel={() => setShowDiscardPortfolioModal(false)}
          onConfirm={onConfirmDiscardPortfolio}
          show={showDiscardPortfolioModal}
          title="PortfolioEditorBuilder.Footer.MultiPortfolios.DiscardMultiPortfoliosModal.Title"
        />
      ) : (
        <DiscardPortfolioModal
          backdropClassName="modal-v2-backdrop"
          onCancel={() => setShowDiscardPortfolioModal(false)}
          onConfirm={onConfirmDiscardPortfolio}
          show={showDiscardPortfolioModal}
        />
      )}

      <SaveUnallocatedPortfolioModal
        backdropClassName="modal-v2-backdrop"
        show={showSaveUnallocatedPortfolioModal}
        onCancel={() => setShowSaveUnallocatedPortfolioModal(false)}
        onSaveAndClose={handleSaveAndClose}
        isSaving={isSaving}
      />

      <ManualProxyReplacementWarningModal
        backdropClassName="modal-v2-backdrop"
        show={showManualProxyReplacementWarningModal}
        onCancel={() => setShowManualProxyReplacementWarningModal(false)}
        onConfirm={onConfirmManualProxyReplacementWarning}
      />

      <ClearHoldingsModal show={showClearHoldingsModal} onClose={hideClearHoldingsModal} />

      {importedAccounts && contact && (
        <>
          <PortfolioEditorPlanAccountModal
            show={showPlanAccountModal}
            onSelectPlanAccount={onAddPlanAccount}
            onClose={onClosePlanAccountModal}
            planId={planId}
            selectedPlanAccountId={planAccountId}
            contact={contact}
          />
          <InformationReplacedModal
            selectedPlanAccountName={selectedPlanAccount?.name || ''}
            show={showInformationReplacedModal}
            onConfirm={onConfirmPlanAccount}
            onCancel={onCancelPlanAccount}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const { editPortfolio, aiStatementReaderMultiPortfolio } = state;
  const missingHoldings = getPortfolioMissingOrInvalidHoldings(selectShelfStatusedPortfolioHoldings(editPortfolio.portfolio)) || [];

  return {
    isSaving: editPortfolio.isSaving,
    isDirty: editPortfolio.isDirty,
    portfolio: editPortfolio.portfolio,
    portfolioHasMissingHoldings: !!missingHoldings.length,
    contact: editPortfolio.portfolio.contact!,
    planId: editPortfolio.portfolio.planning?.planId,
    planAccountId: editPortfolio.portfolio.planning?.planAccountId,
    user: state.auth.user || null,
    editorContext: editPortfolio.editorContext,
    currentHousehold: state.households?.currentHousehold,
    showTaxDetails: editPortfolio.showTaxDetails,
    hasMorePortfolios: hasNextPortfolio(aiStatementReaderMultiPortfolio),
    isInMultiPortfolioMode: isInMultiPortfolioMode(aiStatementReaderMultiPortfolio),
    multiPortfolioCount: aiStatementReaderMultiPortfolio.portfolios.length,
    currentPortfolioIndex: aiStatementReaderMultiPortfolio.currentPortfolioIndex,
  };
};

const mapDispatchToProps = {
  attachProfileToPortfolio,
  setPortfolioPlanAccount,
  setPortfolioSize,
  getFundData,
  getStockData,
  getAltData,
  processNextPortfolio,
  resetMultiPortfolio,
  resetPortfolio,
  addSavedPortfolioId,
};

export default connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(PortfolioEditorBuilderModal));
